import type { CaloriePlanVersion, ExercisePlan, ProgramVersion, TrainingBlock, TrainingPhase } from "./types";

const now = "2026-07-01T00:00:00.000Z";

function exercise(id: string, name: string, target: string, sets: number, notes?: string): ExercisePlan {
  const defaults = parseTargetDefaults(target);
  return {
    id,
    name,
    target,
    sets,
    tracking: "sets",
    defaultReps: defaults.reps,
    defaultSeconds: defaults.seconds,
    notes
  };
}

function checkBlock(id: string, name: string, target: string, notes?: string): ExercisePlan {
  return {
    id,
    name,
    target,
    sets: 1,
    tracking: "block",
    notes
  };
}

export function parseTargetDefaults(target: string): { reps?: number; seconds?: number } {
  const normalized = target.toLowerCase().replace("×", "x");
  const multiplied = normalized.match(/^\s*(\d+)\s*x\s*(\d+)\s*(сек|с\b)?/);
  if (multiplied) {
    const value = Number(multiplied[2]);
    return multiplied[3] ? { seconds: value } : { reps: value };
  }
  const seconds = normalized.match(/^\s*(\d+)\s*сек/);
  if (seconds) return { seconds: Number(seconds[1]) };
  if (normalized.includes("мин") || normalized.includes("макс") || normalized.includes("отказ") || normalized.includes("легко")) return {};
  const plainReps = normalized.match(/^\s*(\d+)(?:\s|$| на)/);
  return plainReps ? { reps: Number(plainReps[1]) } : {};
}

function block(id: string, title: string, timeOfDay: "morning" | "evening", exercises: ExercisePlan[], notes?: string): TrainingBlock {
  return { id, title, timeOfDay, notes, exercises };
}

export const seedProgram: ProgramVersion = {
  id: "program-home-fat-loss-v1",
  name: "Домашное похудение 2 месяца",
  startsOn: "2026-07-01",
  endsOn: "2026-08-25",
  cycleLengthDays: 56,
  createdAt: now,
  updatedAt: "2026-07-12T21:00:00.000Z",
  revision: 3,
  assumptions: [
    "Документ описывает утро + вечер; таблицы 1-5 интерпретированы как вечерние силовые блоки для фаз 1-2, 3-4, 5, 6-7 и 8 недель.",
    "Воскресный вечер заменяется восстановительным блоком ходьбы и растяжки, как указано в тексте каждой фазы.",
    "Утренняя разминка применяется каждый день перед основным утренним блоком.",
    "Если упражнение задано временем или максимумом, приложение фиксирует его как плановую цель без выдуманной прогрессии веса."
  ],
  warmup: [
    checkBlock("warmup", "Разминка", "3 мин", "Вращения руками 20 сек; наклоны корпуса 20 сек; вращения тазом 20 сек; ходьба с подъемом колен 60 сек; jumping jacks 40 сек.")
  ],
  phases: []
};

const phaseDefinitions: TrainingPhase[] = [
  {
    id: "weeks-1-2",
    name: "Недели 1-2: Вход",
    weekStart: 1,
    weekEnd: 2,
    calorieTarget: 2300,
    morning: block("m-w1", "Утро: вход", "morning", [
      checkBlock("w1-high-knees", "Ходьба на месте с высоким подъемом колен", "20 мин", "Спокойный темп, ровное дыхание, можно под видео/подкаст."),
      checkBlock("w1-abs-circuit", "Пресс", "3 круга без отдыха", "Скручивания лёжа 15; планка на локтях 20 сек; подъемы прямых ног лёжа 12.")
    ]),
    evening: block("e-w1", "Вечер: силовой блок", "evening", [
      exercise("w1-knee-pushups", "Отжимания с колен", "4 x 12", 4),
      exercise("w1-squats", "Приседания, таз максимально вниз", "4 x 20", 4),
      exercise("w1-lunges", "Выпады на месте", "3 x 10 на ногу", 3),
      exercise("w1-bridge", "Ягодичный мостик", "3 x 15", 3),
      exercise("w1-elbow-plank", "Планка на локтях", "3 x 30 сек", 3)
    ], "За час до сна."),
    sundayEvening: block("r-w1", "Воскресенье: восстановление", "evening", [checkBlock("w1-walk-stretch", "Ходьба + растяжка", "30 мин + 10 мин")])
  },
  {
    id: "weeks-3-4",
    name: "Недели 3-4: Наращивание",
    weekStart: 3,
    weekEnd: 4,
    calorieTarget: 2100,
    morning: block("m-w3", "Утро: 8 кругов по 2 мин", "morning", [
      checkBlock("w3-cardio-circuit", "Основной блок", "8 кругов по 2 мин", "В каждом круге: бег на месте 60 сек; jumping jacks 30 сек; ходьба на месте 30 сек."),
      checkBlock("w3-abs-circuit", "Пресс", "3 круга без отдыха", "Велосипед 20; планка 30 сек; русские скручивания сидя 20.")
    ]),
    evening: block("e-w3", "Вечер: силовой блок", "evening", [
      exercise("w3-knee-pushups", "Отжимания с колен", "4 x 15", 4, "Кто может, начинает классические на 5-6."),
      exercise("w3-squats", "Приседания", "4 x 25", 4),
      exercise("w3-back-lunges", "Обратные выпады", "3 x 12 на ногу", 3),
      exercise("w3-bridge", "Ягодичный мостик", "4 x 18", 4),
      exercise("w3-superman", "Супермен", "3 x 15", 3),
      exercise("w3-plank-evening", "Планка", "3 x 40 сек", 3)
    ], "За час до сна."),
    sundayEvening: block("r-w3", "Воскресенье: восстановление", "evening", [checkBlock("w3-walk-stretch", "Ходьба + растяжка", "40 мин + растяжка")])
  },
  {
    id: "week-5",
    name: "Неделя 5: Пик объема",
    weekStart: 5,
    weekEnd: 5,
    calorieTarget: 2100,
    morning: block("m-w5", "Утро: 10 кругов по 90 сек", "morning", [
      checkBlock("w5-cardio-circuit", "Основной блок", "10 кругов по 90 сек", "В каждом круге: бёрпи без отжимания 30 сек; ходьба на месте 60 сек."),
      checkBlock("w5-abs-circuit", "Пресс", "3 круга без отдыха", "Скручивания 20; боковая планка 20 сек на каждую сторону; подъемы ног 15.")
    ]),
    evening: block("e-w5", "Вечер: силовой блок", "evening", [
      exercise("w5-pushups", "Отжимания", "4 подхода", 4, "Классика сколько сможешь, добить с колен до 15."),
      exercise("w5-squats", "Приседания", "4 x 30", 4),
      exercise("w5-bulgarian", "Болгарские выпады", "3 x 10 на ногу", 3),
      exercise("w5-single-bridge", "Ягодичный мостик на одной ноге", "3 x 12", 3),
      exercise("w5-superman", "Супермен", "3 x 18", 3),
      exercise("w5-plank", "Планка", "3 x 50 сек", 3)
    ], "За час до сна."),
    sundayEvening: block("r-w5", "Воскресенье: восстановление", "evening", [checkBlock("w5-walk", "Ходьба", "45 мин")])
  },
  {
    id: "weeks-6-7",
    name: "Недели 6-7: Максимальный дефицит",
    weekStart: 6,
    weekEnd: 7,
    calorieTarget: 2000,
    morning: block("m-w6", "Утро: HIIT 12 кругов", "morning", [
      checkBlock("w6-hiit-circuit", "HIIT", "12 кругов по 60 сек", "40 сек работа / 20 сек отдых. Порядок: бёрпи -> прыжки из приседа -> скалолаз -> jumping jacks; повторить 3 цикла."),
      checkBlock("w6-abs-circuit", "Пресс", "3 круга без отдыха", "Велосипед 25; планка 50 сек; боковая планка 25 сек на сторону.")
    ], "Порядок HIIT: бёрпи -> прыжки -> скалолаз -> звёздочка, 3 цикла."),
    evening: block("e-w6", "Вечер: силовой блок", "evening", [
      exercise("w6-pushups", "Отжимания", "5 подходов до почти отказа", 5),
      exercise("w6-pause-squats", "Приседания с паузой внизу 2 сек", "4 x 25", 4),
      exercise("w6-bulgarian", "Болгарские выпады", "4 x 12 на ногу", 4),
      exercise("w6-bridge", "Ягодичный мостик", "4 x 20", 4),
      exercise("w6-superman", "Супермен", "3 x 20", 3),
      exercise("w6-plank-combo", "Планка + боковая планка", "по 3 подхода", 3)
    ], "За час до сна."),
    sundayEvening: block("r-w6", "Воскресенье: восстановление", "evening", [checkBlock("w6-walk-stretch", "Ходьба + растяжка", "45-50 мин + растяжка")])
  },
  {
    id: "week-8",
    name: "Неделя 8: Разгрузка и закрепление",
    weekStart: 8,
    weekEnd: 8,
    calorieTarget: 2250,
    morning: block("m-w8", "Утро: разгрузка", "morning", [
      checkBlock("w8-run", "Бег на месте средней интенсивности", "20 мин", "Ровный пульс, объем снижен примерно на 40%."),
      checkBlock("w8-abs-circuit", "Пресс", "3 круга без отдыха", "Скручивания 15; планка 40 сек.")
    ]),
    evening: block("e-w8", "Вечер: легкий силовой блок", "evening", [
      exercise("w8-pushups", "Отжимания", "3 x макс", 3),
      exercise("w8-squats", "Приседания", "3 x 20", 3),
      exercise("w8-lunges", "Выпады", "3 x 10", 3),
      exercise("w8-bridge", "Ягодичный мостик", "3 x 15", 3),
      exercise("w8-plank-evening", "Планка", "3 x 40 сек", 3)
    ], "За час до сна."),
    sundayEvening: block("r-w8", "Воскресенье: восстановление", "evening", [checkBlock("w8-walk-stretch", "Ходьба + полная растяжка", "легко")])
  }
];

seedProgram.phases = phaseDefinitions;

export const seedCaloriePlan: CaloriePlanVersion = {
  id: "calories-home-fat-loss-v1",
  startsOn: "2026-07-01",
  endsOn: "2026-08-25",
  createdAt: now,
  updatedAt: now,
  revision: 1,
  minKcal: 2000,
  proteinGrams: "150-180 г/день",
  fatGrams: "не ниже 60 г/день",
  water: "2.5-3 л/день",
  targets: [
    { weekStart: 1, weekEnd: 2, kcal: 2300, note: "Вход, дефицит около 550 ккал." },
    { weekStart: 3, weekEnd: 5, kcal: 2100, note: "Наращивание и пик объема, дефицит около 750 ккал." },
    { weekStart: 6, weekEnd: 7, kcal: 2000, note: "Максимальный дефицит; ниже не опускаться." },
    { weekStart: 8, weekEnd: 8, kcal: 2250, note: "Разгрузка и закрепление." }
  ]
};
